import { useMemo, useState } from 'react';

import {
  AVERAGE_CHECK,
  AVERAGE_OCCUPANCY,
  PLAN,
  RECORDS_ALL,
  RECORDS_CANCELED,
  RECORDS_CONFIRMED,
  RECORDS_WAITING,
  REVENUE,
  RECEIPTS_TO_CASH_REGISTER,
  RECORD_CREATED_COUNT,
  REVENUE_TEXT,
  PLAN_TEXT,
  AVERAGE_CHECK_TEXT,
  AVERAGE_OCCUPANCY_TEXT,
  RECORDS_ALL_TEXT,
  RECORDS_CANCELED_TEXT,
  RECORDS_CONFIRMED_TEXT,
  RECORDS_WAITING_TEXT,
  RECEIPTS_TO_CASH_REGISTER_TEXT,
  RECORD_CREATED_AMOUNT,
  FIRST_CLIENTS,
  SECOND_CLIENTS,
  RETURNABILITY,
  AVERAGE_BILL_BY_FIRST_CLIENTS,
  AVERAGE_BILL_BY_SECOND_CLIENTS,
  CLIENTS_WIDGET_ID,
  CLIENTS_WIDGET_TEXT,
} from '@app/constants';
import type { NAnalytics } from '@app/types';

export const useWidgetsConfig = () => {
  const [revenueWidget, setRevenueWidget] =
    useState<NAnalytics.ModelAnalyticsWidget>();
  const [isRevenueWidgetLoading, setIsRevenueWidgetLoading] = useState(false);
  const [planWidget, setPlanWidget] =
    useState<NAnalytics.ModelAnalyticsWidget>();
  const [isPlanWidgetLoading, setIsPlanWidgetLoading] = useState(false);
  const [averageCheckWidget, setAverageCheckWidget] =
    useState<NAnalytics.ModelAnalyticsWidget>();
  const [isAverageCheckWidgetLoading, setIsAverageCheckWidgetLoading] =
    useState(false);
  const [averageOccupancyWidget, setAverageOccupancyWidget] =
    useState<NAnalytics.ModelAnalyticsWidget>();
  const [isAverageOccupancyWidgetLoading, setIsAverageOccupancyWidgetLoading] =
    useState(false);
  const [clientsWidget, setClientsWidget] =
    useState<NAnalytics.ModelAnalyticsWidget>();
  const [isClientsWidgetLoading, setIsClientsWidgetLoading] = useState(false);
  const [recordsAllWidget, setRecordsAllWidget] =
    useState<NAnalytics.ModelAnalyticsWidget>();
  const [isRecordsAllWidgetLoading, setIsRecordsAllWidgetLoading] =
    useState(false);
  const [recordsCanceledWidget, setRecordsCanceledWidget] =
    useState<NAnalytics.ModelAnalyticsWidget>();
  const [isRecordsCanceledWidgetLoading, setIsRecordsCanceledWidgetLoading] =
    useState(false);
  const [recordsConfirmedWidget, setRecordsConfirmedWidget] =
    useState<NAnalytics.ModelAnalyticsWidget>();
  const [isRecordsConfirmedWidgetLoading, setIsRecordsConfirmedWidgetLoading] =
    useState(false);
  const [recordsWaitingWidget, setRecordsWaitingWidget] =
    useState<NAnalytics.ModelAnalyticsWidget>();
  const [isRecordsWaitingWidgetLoading, setIsRecordsWaitingWidgetLoading] =
    useState(false);
  const [receiptsToCashRegisterWidget, setReceiptsToCashRegisterWidget] =
    useState<NAnalytics.ModelAnalyticsWidget>();
  const [
    isReceiptsToCashRegisterWidgetLoading,
    setIsReceiptsToCashRegisterWidgetLoading,
  ] = useState(false);
  const [recordCreatedCountWidget, setRecordCreatedCountWidget] =
    useState<NAnalytics.ModelAnalyticsWidget>();
  const [isRecordCreatedCountLoading, setIsRecordCreatedCountLoading] =
    useState(false);
  const [recordCreatedAmountWidget, setRecordCreatedAmountWidget] =
    useState<NAnalytics.ModelAnalyticsWidget>();
  const [isRecordCreatedAmountLoading, setIsRecordCreatedAmountLoading] =
    useState(false);
  const [firstClientsWidget, setFirstClientsWidget] =
    useState<NAnalytics.ModelAnalyticsWidget>();
  const [isFirstClientsAmountLoading, setIsFirstClientsLoading] =
    useState(false);
  const [secondClientsWidget, setSecondClientsWidget] =
    useState<NAnalytics.ModelAnalyticsWidget>();
  const [isSecondClientsAmountLoading, setIsSecondClientsLoading] =
    useState(false);
  const [returnabilityWidget, setReturnabilityWidget] =
    useState<NAnalytics.ModelAnalyticsWidget>();
  const [isReturnabilityLoading, setIsReturnabilityLoading] = useState(false);
  const [averageBillByFirstClientsWidget, setAverageBillByFirstClientsWidget] =
    useState<NAnalytics.ModelAnalyticsWidget>();
  const [
    isAverageBillByFirstClientsLoading,
    setIsAverageBillByFirstClientsLoading,
  ] = useState(false);
  const [
    averageBillBySecondClientsWidget,
    setAverageBillBySecondClientsWidget,
  ] = useState<NAnalytics.ModelAnalyticsWidget>();
  const [
    isAverageBillBySecondClientsLoading,
    setIsAverageBillBySecondClientsLoading,
  ] = useState(false);

  const financialIndicatorsGroup = useMemo<NAnalytics.WidgetConfig[]>(
    () => [
      {
        widgetId: RECEIPTS_TO_CASH_REGISTER,
        setData: setReceiptsToCashRegisterWidget,
        setLoading: setIsReceiptsToCashRegisterWidgetLoading,
        title: RECEIPTS_TO_CASH_REGISTER_TEXT,
        infoText: 'Сумма всех касс филиала',
        data: receiptsToCashRegisterWidget,
        isLoading: isReceiptsToCashRegisterWidgetLoading,
      },
      {
        widgetId: REVENUE,
        setData: setRevenueWidget,
        setLoading: setIsRevenueWidgetLoading,
        title: REVENUE_TEXT,
        infoText: 'Суммарный доход',
        data: revenueWidget,
        isLoading: isRevenueWidgetLoading,
      },
      {
        widgetId: PLAN,
        setData: setPlanWidget,
        setLoading: setIsPlanWidgetLoading,
        title: PLAN_TEXT,
        infoText: 'Выполнение плана',
        data: planWidget,
        isLoading: isPlanWidgetLoading,
      },
      {
        widgetId: AVERAGE_OCCUPANCY,
        setData: setAverageOccupancyWidget,
        setLoading: setIsAverageOccupancyWidgetLoading,
        title: AVERAGE_OCCUPANCY_TEXT,
        infoText: 'Средняя заполняемость рабочего дня',
        mainValueSign: '%',
        data: averageOccupancyWidget,
        isLoading: isAverageOccupancyWidgetLoading,
      },
    ],
    [
      averageOccupancyWidget,
      isAverageOccupancyWidgetLoading,
      isPlanWidgetLoading,
      isReceiptsToCashRegisterWidgetLoading,
      isRevenueWidgetLoading,
      planWidget,
      receiptsToCashRegisterWidget,
      revenueWidget,
    ]
  );

  const recordsGroup = useMemo<NAnalytics.WidgetConfig[]>(
    () => [
      {
        widgetId: RECORDS_ALL,
        setData: setRecordsAllWidget,
        setLoading: setIsRecordsAllWidgetLoading,
        title: RECORDS_ALL_TEXT,
        infoText: 'Суммарное количество',
        data: recordsAllWidget,
        isLoading: isRecordsAllWidgetLoading,
      },
      {
        widgetId: RECORDS_CANCELED,
        setData: setRecordsCanceledWidget,
        setLoading: setIsRecordsCanceledWidgetLoading,
        title: RECORDS_CANCELED_TEXT,
        infoText: 'Суммарное количество',
        data: recordsCanceledWidget,
        isLoading: isRecordsCanceledWidgetLoading,
      },
      {
        widgetId: RECORDS_CONFIRMED,
        setData: setRecordsConfirmedWidget,
        setLoading: setIsRecordsConfirmedWidgetLoading,
        title: RECORDS_CONFIRMED_TEXT,
        infoText: 'Суммарное количество',
        data: recordsConfirmedWidget,
        isLoading: isRecordsConfirmedWidgetLoading,
      },
      {
        widgetId: RECORDS_WAITING,
        setData: setRecordsWaitingWidget,
        setLoading: setIsRecordsWaitingWidgetLoading,
        title: RECORDS_WAITING_TEXT,
        infoText: 'Суммарное количество',
        data: recordsWaitingWidget,
        isLoading: isRecordsWaitingWidgetLoading,
      },
      {
        widgetId: RECORD_CREATED_COUNT,
        setData: setRecordCreatedCountWidget,
        setLoading: setIsRecordCreatedCountLoading,
        title: 'Записей создано',
        infoText: 'Количество созданных записей',
        data: recordCreatedCountWidget,
        isLoading: isRecordCreatedCountLoading,
      },
      {
        widgetId: RECORD_CREATED_AMOUNT,
        setData: setRecordCreatedAmountWidget,
        setLoading: setIsRecordCreatedAmountLoading,
        title: 'Записей создано на сумму',
        infoText: 'Сумма',
        data: recordCreatedAmountWidget,
        isLoading: isRecordCreatedAmountLoading,
      },
    ],
    [
      isRecordCreatedAmountLoading,
      isRecordCreatedCountLoading,
      isRecordsAllWidgetLoading,
      isRecordsCanceledWidgetLoading,
      isRecordsConfirmedWidgetLoading,
      isRecordsWaitingWidgetLoading,
      recordCreatedAmountWidget,
      recordCreatedCountWidget,
      recordsAllWidget,
      recordsCanceledWidget,
      recordsConfirmedWidget,
      recordsWaitingWidget,
    ]
  );

  const clientsGroup = useMemo<NAnalytics.WidgetConfig[]>(
    () => [
      {
        widgetId: FIRST_CLIENTS,
        setData: setFirstClientsWidget,
        setLoading: setIsFirstClientsLoading,
        title: 'Новых клиентов',
        infoText: 'Количество новых клиентов',
        data: firstClientsWidget,
        isLoading: isFirstClientsAmountLoading,
      },
      {
        widgetId: AVERAGE_BILL_BY_FIRST_CLIENTS,
        setData: setAverageBillByFirstClientsWidget,
        setLoading: setIsAverageBillByFirstClientsLoading,
        title: 'Средний чек для первичных клиентов',
        infoText: 'Сумма',
        data: averageBillByFirstClientsWidget,
        isLoading: isAverageBillByFirstClientsLoading,
      },
      {
        widgetId: SECOND_CLIENTS,
        setData: setSecondClientsWidget,
        setLoading: setIsSecondClientsLoading,
        title: 'Повторных клиентов',
        infoText: 'Количество повторных клиентов',
        data: secondClientsWidget,
        isLoading: isSecondClientsAmountLoading,
      },
      {
        widgetId: AVERAGE_BILL_BY_SECOND_CLIENTS,
        setData: setAverageBillBySecondClientsWidget,
        setLoading: setIsAverageBillBySecondClientsLoading,
        title: 'Средний чек для повторных клиентов',
        infoText: 'Сумма',
        data: averageBillBySecondClientsWidget,
        isLoading: isAverageBillBySecondClientsLoading,
      },
      // Поступило требование убрать этот виджет
      // Закомментировал его на случай, если нужно будет вернуть
      // {
      //   widgetId: REVENUE_BY_SERVICE,
      //   setData: setRevenueByServiceWidget,
      //   setLoading: setIsRevenueByServiceWidgetLoading,
      //   title: 'Выручка по услугам',
      //   infoText: 'Доход по категориям услуг',
      //   data: revenueByServiceWidget,
      //   isLoading: isRevenueByServiceWidgetLoading,
      // },
      {
        widgetId: CLIENTS_WIDGET_ID,
        setData: setClientsWidget,
        setLoading: setIsClientsWidgetLoading,
        title: CLIENTS_WIDGET_TEXT,
        infoText: 'Суммарное количество',
        data: clientsWidget,
        isLoading: isClientsWidgetLoading,
      },
      {
        widgetId: AVERAGE_CHECK,
        setData: setAverageCheckWidget,
        setLoading: setIsAverageCheckWidgetLoading,
        title: AVERAGE_CHECK_TEXT,
        infoText: 'Средний чек за посещение',
        data: averageCheckWidget,
        isLoading: isAverageCheckWidgetLoading,
      },
      {
        widgetId: RETURNABILITY,
        setData: setReturnabilityWidget,
        setLoading: setIsReturnabilityLoading,
        title: 'Возвращаемость',
        data: returnabilityWidget,
        isLoading: isReturnabilityLoading,
      },
    ],
    [
      averageBillByFirstClientsWidget,
      averageBillBySecondClientsWidget,
      averageCheckWidget,
      clientsWidget,
      firstClientsWidget,
      isAverageBillByFirstClientsLoading,
      isAverageBillBySecondClientsLoading,
      isAverageCheckWidgetLoading,
      isClientsWidgetLoading,
      isFirstClientsAmountLoading,
      isReturnabilityLoading,
      isSecondClientsAmountLoading,
      returnabilityWidget,
      secondClientsWidget,
    ]
  );

  const widgetConfig = useMemo(
    () => [
      {
        name: 'Финансовые показатели',
        widgets: financialIndicatorsGroup,
        className: 'financial-indicators-group',
      },
      {
        name: 'Записи',
        widgets: recordsGroup,
        className: 'records-group',
      },
      {
        name: 'Клиенты',
        widgets: clientsGroup,
        className: 'clients-group',
      },
    ],
    [clientsGroup, financialIndicatorsGroup, recordsGroup]
  );

  return {
    widgetConfig,
  };
};
